import React from 'react';
import styled, { css } from 'styled-components';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { TypographyProps } from '@mui/material/Typography';

import ModalHeader from '@ui-parts/ModalHeader';
import ModalBody from '@ui-parts/ModalBody';
import ModalFooter from '@ui-parts/ModalFooter';

import { PALETTE } from '~/assets/styles/base';

const StyledModal = styled(Modal)`
  outline: none;
  &&&:focus {
    outline: 'none !important';
  }
`;

type ModalBoxProps = {
  $width?: string;
};
const ModalBox = styled(Box)<ModalBoxProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${PALETTE.white};
  box-shadow: 10px 10px 10px #00000027;
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
    `}
`;

const CloseButtonBox = styled(Box)`
  position: absolute;
  top: -45px;
  right: -45px;
  color: ${PALETTE.white};
`;

const CloseIconStyle = {
  fontSize: '4rem',
  color: `${PALETTE.white}`,
};

type Props = {
  children: React.ReactNode;
  open: boolean;
  titleProps: {
    titleLabelVariant?: TypographyProps['variant'];
    titleVariant?: TypographyProps['variant'];
    descVariant?: TypographyProps['variant'];
    titleLabel?: string;
    title?: string;
    desc?: string;
    customModalTitle?: JSX.Element;
  };
  buttons?: JSX.Element[];
  aboveButtons?: JSX.Element;
  underButtons?: JSX.Element;
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId: string;
  disableAutoFocus?: boolean;
  width?: string;
  scrollable?: boolean;
  border?: string;
};

const BasicModal: React.FC<Props> = ({
  children,
  open,
  titleProps,
  buttons,
  aboveButtons,
  underButtons,
  handleClose,
  dataTestId = 'uiParts-BasicModal',
  disableAutoFocus = true,
  width,
  scrollable = true,
  border = '',
}) => (
  <StyledModal
    open={open}
    onClose={handleClose}
    disableAutoFocus={disableAutoFocus}
    data-testid={dataTestId}
  >
    <ModalBox $width={width}>
      <CloseButtonBox>
        <IconButton onClick={handleClose} data-testid="uiParts-BasicModalClose">
          <CloseIcon style={CloseIconStyle} />
        </IconButton>
      </CloseButtonBox>
      <div style={{ position: 'relative' }}>
        <ModalHeader titleProps={titleProps} />
        <ModalBody scrollable={scrollable} border={border}>
          {children}
        </ModalBody>
        <ModalFooter buttons={buttons} aboveButtons={aboveButtons} underButtons={underButtons} />
      </div>
    </ModalBox>
  </StyledModal>
);

export default BasicModal;
